// src/useCase/billingUseCase.js
import * as billingApi from "../api/billingService";
import { getAccountInfo } from "../utils/authToken";

const BILLING_SUCCESS_PATH = "/billing/success";
const BILLING_CANCEL_PATH = "/upgrade";

function getAppBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BASE_URL || "";
}

function buildReturnUrl(path) {
  return `${getAppBaseUrl()}${path}`;
}

function normalizeCheckoutResponse(response) {
  const data = response?.data ?? response ?? {};
  return {
    ...data,
    checkoutUrl: data.checkoutUrl || data.url || null,
  };
}

const billingPlansCache = new Map();

function getBillingPlansCacheKey() {
  const accountInfo = getAccountInfo() || {};
  const accountId = Number(accountInfo?.accountId);

  if (Number.isFinite(accountId) && accountId > 0) {
    return `account:${Math.trunc(accountId)}`;
  }

  return "account:anonymous";
}

async function fetchBillingPlansData(forceRefresh = false) {
  const cacheKey = getBillingPlansCacheKey();

  if (!forceRefresh && billingPlansCache.has(cacheKey)) {
    return billingPlansCache.get(cacheKey);
  }

  const response = await billingApi.getBillingPlans();
  const data = response?.data ?? response ?? [];
  const normalizedPlans = Array.isArray(data) ? data : [];
  billingPlansCache.set(cacheKey, normalizedPlans);
  return normalizedPlans;
}

function normalizePlanLookupToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function stripPlanVariantSuffix(value) {
  return String(value || "")
    .replace(/_(BRL|USD|EUR)$/, "")
    .replace(/_(MONTHLY|YEARLY)$/, "");
}

function normalizePlanAlias(value) {
  return String(value || "").trim().toUpperCase();
}

function extractCurrencySuffix(value) {
  const match = String(value || "").toUpperCase().match(/_(BRL|USD|EUR)$/);
  return match?.[1] || null;
}

async function getPlanIdOrThrow(planCode) {
  if (!planCode) {
    throw new Error('Plano não informado.');
  }

  const plans = await fetchBillingPlansData();
  const requestedToken = normalizePlanLookupToken(planCode);
  const requestedBaseCode = normalizePlanAlias(
    stripPlanVariantSuffix(requestedToken),
  );
  const requestedCurrency = extractCurrencySuffix(requestedToken);

  const matchedPlanByExactCode = plans.find(
    (plan) => normalizePlanLookupToken(plan?.code) === requestedToken,
  );

  const matchedPlanByBaseAndCurrency = plans.find((plan) => {
    const planToken = normalizePlanLookupToken(plan?.code);
    const planBaseCode = normalizePlanAlias(stripPlanVariantSuffix(planToken));
    const planCurrency = extractCurrencySuffix(planToken);
    return (
      planBaseCode === requestedBaseCode &&
      (!requestedCurrency || planCurrency === requestedCurrency)
    );
  });

  const matchedPlanByBaseCode = plans.find((plan) => {
    const planToken = normalizePlanLookupToken(plan?.code);
    const planBaseCode = normalizePlanAlias(stripPlanVariantSuffix(planToken));
    return planBaseCode === requestedBaseCode;
  });

  const matchedPlan =
    matchedPlanByExactCode || matchedPlanByBaseAndCurrency || matchedPlanByBaseCode;

  const planId = Number(matchedPlan?.planId);
  if (!Number.isFinite(planId) || planId <= 0) {
    throw new Error(`Plano ${planCode} não encontrado para o Stripe.`);
  }

  return planId;
}

// Listar planos de cobrança
export async function getBillingPlans() {
  return fetchBillingPlansData();
}

export async function getBillingStatus() {
  return billingApi.getBillingStatus();
}

/**
 * Inicia checkout:
 * - ASAAS: mantém endpoint legado
 * - STRIPE: usa /api/v1/billing/checkout e normaliza a resposta
 */
export async function checkoutSession(checkoutSessionRequest) {
  const provider = checkoutSessionRequest?.provider || "ASAAS";

  if (provider === "STRIPE") {
    const planId = await getPlanIdOrThrow(
      checkoutSessionRequest?.planCode,
    );

    const payload = {
      planId,
      recurrence: checkoutSessionRequest?.recurrence,
      successUrl: buildReturnUrl(BILLING_SUCCESS_PATH),
      cancelUrl: buildReturnUrl(BILLING_CANCEL_PATH),
      hasEngagersAddon: !!checkoutSessionRequest?.hasEngagersAddon,
    };

    const result = await billingApi.stripeCheckout(payload);
    return normalizeCheckoutResponse(result);
  }

  const result = await billingApi.checkoutSession(checkoutSessionRequest);
  return normalizeCheckoutResponse(result);
}

// Billing summary legado (mantido para não quebrar)
export async function loadBillingSummary() {
  return billingApi.loadBillingSummary();
}

export async function loadBillingInvoices() {
  return billingApi.getBillingInvoices();
}

/**
 * Upgrade:
 * - ASAAS: mantém endpoint legado
 * - STRIPE: usa o mesmo /api/v1/billing/checkout com targetPlanCode
 */
export async function upgradeCheckoutSession(payload) {
  const provider = payload?.provider || "ASAAS";

  if (provider === "STRIPE") {
    const planId = await getPlanIdOrThrow(payload?.targetPlanCode);

    const stripePayload = {
      planId,
      recurrence: payload?.targetRecurrence,
      successUrl: buildReturnUrl(BILLING_SUCCESS_PATH),
      cancelUrl: buildReturnUrl(BILLING_CANCEL_PATH),
      hasEngagersAddon: !!payload?.hasEngagersAddon,
    };

    const result = await billingApi.stripeCheckout(stripePayload);
    return normalizeCheckoutResponse(result);
  }

  const result = await billingApi.upgradeCheckoutSession(payload);
  return normalizeCheckoutResponse(result);
}

export async function cancelSubscription() {
  return billingApi.cancelSubscription();
}

export async function resumeSubscription() {
  return billingApi.resumeSubscription();
}

export async function cancelScheduledDowngrade() {
  return billingApi.cancelScheduledDowngrade();
}

// STRIPE: status do checkout por session_id
export async function getStripeCheckoutStatus(sessionId) {
  if (!sessionId) {
    return { data: null };
  }
  return billingApi.stripeCheckoutStatus(sessionId);
}

// STRIPE: abrir customer portal
export async function openStripeBillingPortal(returnUrl) {
  const safeReturnUrl =
    returnUrl || (typeof window !== "undefined" ? window.location.href : "");

  const result = await billingApi.stripePortal({ returnUrl: safeReturnUrl });
  const data = result?.data ?? result ?? {};
  return {
    ...data,
    url: data.url || null,
  };
}
